import React, { FC, useEffect, useState } from 'react'
import {
  Checkbox,
  Radio,
  RadioGroup,
  Toggle
} from '@contrast-security-inc/design-system-inputs'
import { LicenseThermometerSkeletor } from '../../thermometer/license-thermometer-skeletor'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { MessageBanner } from '@contrast-security-inc/design-system-alerts'

interface ProtectServerSettings {
  defend: boolean
  botProtection: boolean
  syslogEnabled: boolean
  autoLicenseProtection: boolean
}

interface ProtectLicenseBreakdown {
  total: number
  used: number
}

export interface ProtectServerDefaultsFormProps {
  formData: ProtectServerSettings
  licenseBreakdown: ProtectLicenseBreakdown
  onChange: (...args) => void
}

const mapReactStateToAngularState = (state: ProtectServerSettings) => {
  const { syslogEnabled, ...rest } = state
  return { ...rest, syslog_enabled: syslogEnabled }
}

const licenseStyle = css`
  width: 50%;
  margin-bottom: 32px;
`

const layout = css`
  margin-bottom: 16px;
`

const checkboxGroupLayout = css`
  display: flex;
  flex-direction: column;
`

export const ProtectServerDefaultsForm: FC<ProtectServerDefaultsFormProps> = ({
  formData,
  licenseBreakdown,
  onChange
}) => {
  const { formatMessage } = useIntl()
  const [state, setState] = useState<ProtectServerSettings>(formData)

  useEffect(() => {
    setState(formData)
  }, [formData])

  const handleChange = (newValues: Partial<ProtectServerSettings>) => {
    const newState = { ...state, ...newValues }
    setState(newState)
    onChange(mapReactStateToAngularState(newState))
  }

  const { defend, botProtection, autoLicenseProtection, syslogEnabled } = state
  const { total, used } = licenseBreakdown

  const excess = used - total
  const totalUsed = excess > 0 ? total : used
  const customLabelString = `${totalUsed}/${total}`

  return (
    <div>
      <Toggle
        className={layout}
        isChecked={defend}
        label={formatMessage({ id: 'title_protect' })}
        onChange={() =>
          handleChange({ defend: !defend, autoLicenseProtection: !defend })
        }
      />

      <MessageBanner
        testId="protect-toggle-message-banner"
        className={css`
          margin-bottom: 16px;
        `}
        message={formatMessage({
          id: 'detail_effectiveConfig_overridesProtectLicesing'
        })}
        variant="info"
        inline
      />

      <RadioGroup name="autolicense" isDisabled={defend} className={layout}>
        <Radio
          label={formatMessage({
            id: 'action_servers_autoLicenseProtect_enable'
          })}
          isChecked={autoLicenseProtection}
          onChange={() =>
            handleChange({ autoLicenseProtection: !autoLicenseProtection })
          }
        />
        <Radio
          label={formatMessage({
            id: 'action_servers_autoLicenseProtect_disable'
          })}
          isChecked={!autoLicenseProtection}
          onChange={() =>
            handleChange({ autoLicenseProtection: !autoLicenseProtection })
          }
        />
      </RadioGroup>
      <LicenseThermometerSkeletor
        className={licenseStyle}
        total={total}
        used={{
          value: totalUsed
        }}
        excess={excess > 0 ? { value: excess } : undefined}
        size="sm"
        customLabel={`${customLabelString} ${formatMessage({
          id: 'detail_licensesUsed'
        })}`}
      />
      <div className={checkboxGroupLayout}>
        <Checkbox
          className={layout}
          label={formatMessage({
            id: 'detail_serversSettings_enableBotBlocking_checkbox'
          })}
          isChecked={botProtection}
          onChange={() => handleChange({ botProtection: !botProtection })}
        />
        <Checkbox
          className={layout}
          label={formatMessage({
            id: 'detail_enableOutputEventsToSyslog_checkbox'
          })}
          isChecked={syslogEnabled}
          onChange={() => handleChange({ syslogEnabled: !syslogEnabled })}
        />
      </div>
    </div>
  )
}
